import React from 'react';
import { 
  Box,
  Typography,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Build as UtilitiesIcon } from '@mui/icons-material';
import { useFormContext, Controller } from 'react-hook-form';
import StepFormLayout from '../../shared/StepFormLayout';
import FormTextField from '../../shared/FormTextField';

interface UtilitiesStepProps {
  stepProgress: number;
}

const UtilitiesStep: React.FC<UtilitiesStepProps> = ({ stepProgress }) => {
  const { control, setValue } = useFormContext();

  const utilities = [
    { name: 'electric', label: 'Electricity' },
    { name: 'gas', label: 'Gas' },
    { name: 'water', label: 'Water' },
    { name: 'sewer', label: 'Sewer/Septic' },
    { name: 'trash', label: 'Trash Collection' },
    { name: 'internet', label: 'Internet' },
    { name: 'cable', label: 'Cable' },
    { name: 'lawnCare', label: 'Lawn Care' },
    { name: 'snowRemoval', label: 'Snow Removal' },
  ];

  const handleRadioChange = (utilityName: string, value: string) => {
    setValue(`utilities.${utilityName}.responsibility`, value);
  };

  return (
    <StepFormLayout
      title="Utilities & Services"
      description="Specify who is responsible for each utility and service"
      icon={<UtilitiesIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      progress={stepProgress}
      showOptionalBadge={true}
    >
      
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utility/Service</TableCell>
              <TableCell align="center">Landlord</TableCell>
              <TableCell align="center">Tenant</TableCell>
              <TableCell align="center">N/A</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {utilities.map((utility) => (
              <TableRow key={utility.name}>
                <TableCell component="th" scope="row">
                  {utility.label}
                </TableCell>
                <Controller
                  name={`utilities.${utility.name}.responsibility`}
                  control={control}
                  defaultValue="na"
                  render={({ field }) => (
                    <>
                      <TableCell align="center">
                        <Radio
                          checked={field.value === 'landlord'}
                          onChange={() => handleRadioChange(utility.name, 'landlord')}
                          value="landlord"
                          name={`utilities.${utility.name}.responsibility`}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={field.value === 'tenant'}
                          onChange={() => handleRadioChange(utility.name, 'tenant')}
                          value="tenant"
                          name={`utilities.${utility.name}.responsibility`}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Radio
                          checked={field.value === 'na'}
                          onChange={() => handleRadioChange(utility.name, 'na')}
                          value="na"
                          name={`utilities.${utility.name}.responsibility`}
                        />
                      </TableCell>
                    </>
                  )}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={3}>
        <Typography variant="subtitle1" gutterBottom>
          Utility Billing Notice
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Specify how many days' notice you will provide before changing any shared utility allocation or billing method.
        </Typography>
        <FormTextField
          name="utilities.billingNoticeDays"
          control={control}
          label="Notice Period (Days)"
          type="number"
          InputProps={{ inputProps: { min: 0 } }}
        />
      </Box>
    </StepFormLayout>
  );
};

export default UtilitiesStep; 
